import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { toast } from 'react-toastify';
import { BsPencil, BsTrash, BsArrowReturnRight, BsGripVertical } from 'react-icons/bs';

const Categories = () => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [name, setName] = useState('');
    const [nameFr, setNameFr] = useState('');
    const [parentId, setParentId] = useState('');
    const [editingCategory, setEditingCategory] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);

    // Kategorileri Backend'den Çek
    const fetchCategories = async () => {
        try {
            const response = await axios.get('/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Kategoriler yüklenirken hata:', error);
        }
    };

    // Sayfa açılınca çalışır
    useEffect(() => {
        fetchCategories();
    }, []);

    // Kategorileri Ağaç Yapısına Göre Sırala ve Düzenle
    const sortedCategories = useMemo(() => {
        const map = {};
        const roots = [];
        // Veriyi bozmamak için kopyasını alalım
        const cats = JSON.parse(JSON.stringify(categories));
        
        // ID'ye göre haritala
        cats.forEach(cat => {
            cat.children = [];
            map[cat.id] = cat;
        });
        
        // Hiyerarşiyi kur
        cats.forEach(cat => {
            if (cat.parent_id && map[cat.parent_id]) {
                map[cat.parent_id].children.push(cat);
            } else {
                roots.push(cat);
            }
        });
        
        // Listeyi düzleştir (flatten) ama sırayı koru
        const flatten = (list, level = 0) => {
            let arr = [];
            list.forEach(item => {
                arr.push({ ...item, level });
                if (item.children.length > 0) {
                    arr = arr.concat(flatten(item.children, level + 1));
                }
            });
            return arr;
        };
        
        return flatten(roots);
    }, [categories]);

    // Yeni Kategori Ekle veya Güncelle
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCategory) {
                await axios.put(`/categories/${editingCategory.id}`, { name, name_fr: nameFr, parent_id: parentId || null });
                toast.success(t('messages.category_updated'));
                setEditingCategory(null);
            } else {
                await axios.post('/categories', { name, name_fr: nameFr, parent_id: parentId || null });
                toast.success(t('messages.category_added'));
            }
            setName(''); // Kutuyu temizle
            setNameFr('');
            setParentId(''); // Seçimi temizle
            fetchCategories(); // Listeyi güncelle
        } catch (error) {
            toast.error(t('messages.error_occurred'));
        }
    };

    // Kategori Sil
    const handleDelete = async (id) => {
        if (window.confirm(t('messages.confirm_delete_category'))) {
            try {
                await axios.delete(`/categories/${id}`);
                toast.success(t('messages.category_deleted'));
                fetchCategories();
            } catch (error) {
                toast.error(t('messages.delete_failed'));
            }
        }
    };

    const startEdit = (category) => {
        setEditingCategory(category);
        setName(category.name);
        setNameFr(category.name_fr || '');
        setParentId(category.parent_id || '');
    };

    const cancelEdit = () => {
        setEditingCategory(null);
        setName('');
        setNameFr('');
        setParentId('');
    };

    // --- Sürükle Bırak İşlemleri ---
    const onDragStart = (e, category) => {
        setDraggedItem(category);
        e.dataTransfer.effectAllowed = "move";
        // Sürüklenen öğenin hayalet görüntüsünü şeffaflaştır (isteğe bağlı)
        // e.dataTransfer.setDragImage(e.target.parentNode, 20, 20);
    };

    const onDragOver = (e) => {
        e.preventDefault(); // Bırakmaya izin ver
    };

    const onDrop = async (e, targetCategory) => {
        e.preventDefault();
        
        if (!draggedItem || draggedItem.id === targetCategory.id) return;

        // Kendisinin alt kategorisine taşınmasını engelle (Döngüsel bağımlılık)
        // Basit kontrol: Eğer hedef kategori, sürüklenen kategorinin altındaysa engelle
        // (Daha kapsamlı kontrol için tüm alt ağacı taramak gerekir)
        
        let newParentId = targetCategory.id;
        
        // Eğer "Ana Kategoriye Taşı" alanına bırakıldıysa
        if (targetCategory === 'root') {
            newParentId = null;
        }

        // Backend'e gönderilecek veriyi hazırla
        // Sadece taşınan kategorinin parent_id'sini güncellemek yeterli olabilir
        // Ancak sıralama da değişecekse tüm listeyi göndermek gerekebilir.
        // Şimdilik sadece parent_id güncelleyelim.

        try {
            // Optimistic UI Update (Arayüzü hemen güncelle)
            const updatedCategories = categories.map(cat => {
                if (cat.id === draggedItem.id) {
                    return { ...cat, parent_id: newParentId };
                }
                return cat;
            });
            setCategories(updatedCategories);

            // Backend'e kaydet
            // Tek bir kategori güncellendiği için updateCategory endpointini kullanabiliriz
            // Veya toplu güncelleme endpointi
            
            // Basit yöntem: Tekil güncelleme
            await axios.put(`/categories/${draggedItem.id}`, { 
                name: draggedItem.name, 
                parent_id: newParentId 
            });
            
            toast.success(t('messages.category_moved'));
            fetchCategories(); // Listeyi yenile (Sıralama ve yapı için)
        } catch (error) {
            toast.error(t('messages.move_failed'));
            fetchCategories(); // Hata olursa eski haline döndür
        }
        setDraggedItem(null);
    };

    return (
        <AdminLayout>
            <h2>{t('categories.title')}</h2>
            
            {/* Ekleme Formu */}
            <div className="card mb-4 mt-3 shadow-sm">
                <div className="card-body">
                    <form onSubmit={handleSubmit} className="d-flex gap-2">
                        <input 
                            type="text" 
                            className="form-control" 
                            placeholder={t('categories.name_tr')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                        <input 
                            type="text" 
                            className="form-control" 
                            placeholder={t('categories.name_fr')}
                            value={nameFr}
                            onChange={(e) => setNameFr(e.target.value)}
                        />
                        <select 
                            className="form-select" 
                            value={parentId} 
                            onChange={(e) => setParentId(e.target.value)}
                            style={{maxWidth: '250px'}}
                        >
                            <option value="">{t('categories.parent_category')}</option>
                            {sortedCategories
                                .filter(cat => cat.id !== editingCategory?.id) // Kendisini üst kategori olarak seçemesin
                                .map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {'\u00A0'.repeat(cat.level * 4)}{cat.name}
                                    </option>
                                ))
                            }
                        </select>
                        {editingCategory ? (
                            <>
                                <button type="submit" className="btn btn-warning text-white">{t('categories.update')}</button>
                                <button type="button" className="btn btn-secondary" onClick={cancelEdit}>{t('categories.cancel')}</button>
                            </>
                        ) : (
                            <button type="submit" className="btn btn-success">{t('categories.add')}</button>
                        )}
                    </form>
                </div>
            </div>

            {/* Liste Tablosu */}
            
            {/* Ana Kategoriye Taşıma Alanı (Drop Zone) */}
            <div 
                className="p-3 mb-3 border border-dashed rounded text-center text-muted bg-light"
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, 'root')}
            >
                {t('categories.drop_zone')}
            </div>

            <div className="table-responsive">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>{t('categories.id')}</th>
                            <th>{t('categories.name')}</th>
                            <th>{t('categories.slug')}</th>
                            <th style={{width: '100px'}}>{t('categories.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedCategories.map((cat) => (
                            <tr 
                                key={cat.id}
                                draggable
                                onDragStart={(e) => onDragStart(e, cat)}
                                onDragOver={onDragOver}
                                onDrop={(e) => onDrop(e, cat)}
                                style={{ cursor: 'move' }}
                            >
                                <td className="text-muted"><BsGripVertical /> {cat.id}</td>
                                <td>
                                    <div style={{ paddingLeft: `${cat.level * 20}px` }}>
                                        {cat.level > 0 && <BsArrowReturnRight className="me-2 text-muted" />}
                                        {cat.name} <small className="text-muted ms-2">({cat.name_fr || '-'})</small>
                                    </div>
                                </td>
                                <td>{cat.slug}</td>
                                <td className="d-flex gap-2">
                                    <button 
                                        className="btn btn-sm btn-outline-primary" 
                                        onClick={() => startEdit(cat)}
                                        title={t('categories.edit')}
                                    >
                                        <BsPencil />
                                    </button>
                                    <button 
                                        className="btn btn-sm btn-danger" 
                                        onClick={() => handleDelete(cat.id)}
                                        title={t('categories.delete')}
                                    >
                                        <BsTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {categories.length === 0 && (
                            <tr>
                                <td colSpan="5" className="text-center p-3">{t('categories.no_data')}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </AdminLayout>
    );
};

export default Categories;
